/* eslint-disable no-param-reassign */
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { isAfter, parseISO, startOfWeek } from 'date-fns';
import { GetTimeSlotsPayload, TimeSlotsState } from './timeSlotsTypes';
import { Slots } from '../../services/Appointment/model';
import { ServiceLocation, startOfWeekDayToNumber } from '../../helpers';
import { services } from '../../context';
import { Errors } from '../../services/Appointment/errors';
import { AppState } from '../app/appTypes';

const initialState: TimeSlotsState = {
  from: new Date().toISOString(),
  prevEnabled: false,
  availableTimeSlots: {},
};

export const selectTimeSlots = createSelector(
  (state: { timeSlots: TimeSlotsState }) => state.timeSlots.availableTimeSlots,
  (state: { app: AppState }) => state.app.selected.storeId,
  (availableTimeSlots, storeId) => availableTimeSlots[storeId] || [],
);

export const getTimeSlots = createAsyncThunk(
  'timeSlots/getTimeSlots',
  async ({
    zipCode,
    serviceId,
    location,
    storeId,
    from,
    serviceCode,
  }: {
    zipCode: string;
    serviceId: string;
    location: ServiceLocation;
    storeId: string;
    from: string;
    serviceCode: string;
  }) => {
    const weekStartsOn = startOfWeekDayToNumber(
      CSS_CONFIG.FORMATS.firstDayOfWeek,
    );
    const firstDayOfWeek = startOfWeek(parseISO(from), { weekStartsOn });
    const prevEnabled = isAfter(firstDayOfWeek, new Date());

    const { result, error } = await services.appointment.getAvailableTimeslots(
      zipCode,
      serviceId,
      location,
      storeId,
      firstDayOfWeek.toISOString(),
      serviceCode,
    );

    if (error === Errors.SERVICE_NOT_FOUND) {
      return {
        storeId,
        from: firstDayOfWeek.toISOString(),
        prevEnabled,
        availableTimeSlots: [],
      };
    }
    if (error) {
      return Promise.reject();
    }

    const reformatDates: Slots[] =
      result.timeSlots &&
      result.timeSlots.map((slot: Slots) => ({
        date:
          typeof slot.date === 'string'
            ? parseISO(slot.date).toISOString()
            : slot.date,
        timeSlots: slot.timeSlots,
      }));

    return {
      storeId,
      from: firstDayOfWeek.toISOString(),
      prevEnabled,
      availableTimeSlots: reformatDates || [],
    };
  },
);

const timeSlotsSlice = createSlice({
  name: 'timeSlots',
  initialState,
  reducers: {
    clearAvailableTimeSlots(state) {
      state.availableTimeSlots = {};
    },
    resetTimeSlotsFromDate(state) {
      state.from = new Date().toISOString();
    },
    updateTimeSlotsStateFromCache(
      state,
      action: PayloadAction<TimeSlotsState>,
    ) {
      return action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getTimeSlots.fulfilled,
      (state, action: PayloadAction<GetTimeSlotsPayload>) => {
        state.from = action.payload.from;
        state.prevEnabled = action.payload.prevEnabled;
        state.availableTimeSlots = {
          [action.payload.storeId]: action.payload.availableTimeSlots,
        };
      },
    );
  },
});

export const {
  clearAvailableTimeSlots,
  resetTimeSlotsFromDate,
  updateTimeSlotsStateFromCache,
} = timeSlotsSlice.actions;

export default timeSlotsSlice.reducer;
